// src/Views/Projects.js
import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "./Projects.css";
import { motion } from "framer-motion";
import { HashLink as Link } from 'react-router-hash-link';

const smoothScroll = (el) => {
  const topOffset = el.getBoundingClientRect().top + window.pageYOffset;
  window.scrollTo({
    top: topOffset,
    behavior: 'smooth', // smooth scrolling
  });
};


const projectsData = [
  {
    id: "project-bi_platform",
    title: "Business Intelligence Product Platform",
    context: "Traditional dashboard and data product development depends heavily on engineering teams, making every requirement change resource-intensive. Customization is slow, costly, and difficult to scale.",
    role: "Led product discovery and requirements gathering across data, engineering, and business teams. Defined and managed the product roadmap, prioritizing features through business value–effort analysis. Facilitated cross-functional workshops to align data strategy, architecture, and visualization goals. Drove agile product delivery and implementation, ensuring iterative value release and stakeholder alignment.",
    solution: "Built a modular BI platform enabling Product Managers to self-serve dashboards and analytical data products customized to user needs. Partnered with engineers to design scalable data product development tools supporting faster deployment and reduced dependency on engineering. Introduced iterative user feedback loops to continuously enhance usability, data visualization, and product customization capabilities.",
    skills: [
      "Effective stakeholder management and cross-functional collaboration",
      "Scaling customization through modular, configurable product architecture",
      "Deep understanding of data visualization strategy and agile delivery",
      "Translating complex data sets into actionable business insights"
    ]
  },
  {
    id: "project-digital_content_platform",
    title: "Digital Content Platform",
    context: "Marketing teams from niche B2B and technical product companies required a unified platform to enhance user engagement and strengthen digital visibility.",
    role: "Led product discovery to identify key content performance indicators and user needs across marketing teams. Collaborated with stakeholders to define KPIs, reporting frameworks, and visualization requirements. Prioritized platform features based on business impact, data availability, and technical feasibility.",
    solution: "Designed and delivered digital content platform with interactive performance dashboard for tracking user engagement, reach, and conversion metrics across technical content pages. Automated technical content creation from technical data sheets to eliminate manual effort and increase digital visibility. Implemented content improvement suggestions to improve performance metrics.",
    skills: [
      "Data-driven marketing strategy",
      "Stakeholder alignment and cross-functional collaboration",
      "Content performance optimization strategy"
    ]
  },
  {
    id: "project-analytical_tools",
    title: "Analytical Tools for Material Scientists",
    context: "R&D teams required a data-driven material discovery and comparison platform to streamline material selection and accelerate project development.",
    role: "Partnered with material scientists to gather detailed requirements and understand research workflows. Designed and delivered tools to support material search and comparison. Continuously analyzed user behavior and platform usage patterns to improve the product experience.",
    solution: "Developed advanced search and filtering capabilities for efficient material discovery. Provided material comparison tool with detailed property analysis and visualization. Iterated on user feedback to improve usability.",
    skills: [
      "Material search processes",
      "User-centered software design",
      "Advanced data visualization for complex material analysis using tools like Ashby charts"
    ]
  },
  {
    id: "project-bi_dashboards",
    title: "BI Dashboards for Franchise Operations",
    context: "Business managers overseeing international operations required real-time business intelligence dashboards and predictive analytics to enhance operational efficiency and decision accuracy across markets.",
    role: "Gathered KPI requirements from managers. Delivered actionable operational insights through BI dashboards to support data-driven decision-making. Led user onboarding initiatives and established continuous improvement processes based on user feedback to enhance dashboard usability and adoption.",
    solution: "Built interactive BI dashboards for operational monitoring. Integrated multiple data sources into a unified view. Implemented automated alerts for key metrics.",
    skills: [
      "Operational Analysis",
      "Dashboard design and development",
      "Cross-team collaboration"
    ]
  },
  {
    id: "project-datasheets",
    title: "Vehicle History Datasheets",
    context: "Car buyers required fast access to accurate and comprehensive vehicle history reports to make informed purchase decisions, enhance transparency in the used-car market, and prevent fraud including odometer tampering and VIN inconsistencies.",
    role: "Defined detailed data requirements for vehicle history reports and compliance with industry standards. Collaborated with data engineering teams to consolidate and normalize automotive datasets from multiple sources. Designed user-friendly, accurate reports to support decision-making and fraud prevention.",
    solution: "Developed a scalable platform for generating comprehensive vehicle history datasheets with actionable insights for buyers and dealerships. Implemented automated quality checks and fraud detection mechanisms to flag anomalies such as odometer rollback, VIN inconsistencies, and accident histories. Produced detailed analytics reports for regulatory agencies and automotive stakeholders.",
    skills: [
      "Automotive data analytics and reporting",
      "Data consolidation and normalization across multiple sources",
      "VIN decoding, fraud detection, and risk assessment",
      "Designing actionable vehicle history reports for buyers and regulatory stakeholders"
    ]
  }
];

const Projects = () => {
  return (
    <div className="projects-wrapper">
      <Header />

      <section className="projects-hero">
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Projects
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          A showcase of complex challenges, innovative solutions, and measurable impact across BI, product, and data-driven platforms.
        </motion.p>
      </section>

      <section className="projects-stack">
        {projectsData.map((project, index) => (
          <motion.div
            key={project.id}
            id={project.id}
            className="project-card"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.2, duration: 0.8 }}
          >
            {/* Optional image can go here later */}
            <h2>{project.title}</h2>
            <h3>Context / Challenge</h3>
            <p>{project.context}</p>
            <h3>Role & Contributions</h3>
            <p>{project.role}</p>
            <h3>Solution Approach</h3>
            <p>{project.solution}</p>
            <h3>Key Learnings / Skills Demonstrated</h3>
            <ul>
              {project.skills.map((skill, i) => (
                <li key={i}>{skill}</li>
              ))}
            </ul>
            <Link to="/#projects" scroll={el => smoothScroll(el)} className="cta-button">
  Back to Main Page
</Link>
          </motion.div>
        ))}
      </section>

      <motion.section
        className="projects-cta"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <h2>Interested in collaborating on impactful projects?</h2>
        <p>
          Whether it’s BI, product platforms, or data solutions, let’s work together
          to create measurable outcomes and meaningful impact.
        </p>
        <a href="/#contact" className="cta-main-button">
          Get in Touch
        </a>
      </motion.section>

      <Footer />
    </div>
  );
};

export default Projects;
